import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { Modal, Button, Form } from 'react-bootstrap';
import OrderFilter from './OrderFilter';
import { API_URL } from '../config/api';
import PageLoader from './PageLoader';

const safeRender = (v) => (v === null || v === undefined ? 'N/A' : typeof v === 'object' ? JSON.stringify(v) : String(v));

const formatDate = (s) => {
  if (!s) return '-';
  try {
    const d = new Date(s.includes('T') ? s.split('T')[0] : s);
    return isNaN(d) ? s : d.toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return s; }
};

const formatCurrency = (n) =>
  !n ? '0.00' : parseFloat(n).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLORS = {
  'Pending Approval': { bg: '#FEF3C7', color: '#92400E' },
  'Approved':         { bg: '#D1FAE5', color: '#065F46' },
  'Rejected':         { bg: '#FEE2E2', color: '#991B1B' },
  'Pending Fulfillment': { bg: '#DBEAFE', color: '#1E40AF' },
  'Pending Billing':  { bg: '#EDE9FE', color: '#5B21B6' },
};
const statusStyle = (s) => STATUS_COLORS[s] || { bg: '#F1F5F9', color: '#475569' };

const SaleCoResponse = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const actionParam = searchParams.get('action');
  const deptParam = searchParams.get('dept');

  const [orderList, setOrderList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [orderStatus, setOrderStatus] = useState('');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({ staffCode: '', status: '', startDate: '', endDate: '', department: '' });

  const department = deptParam || currentUser?.department || '';

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        Object.entries(appliedFilters).forEach(([k, v]) => v && params.append(k, v));
        const res = await axios.get(`${API_URL}/orders/all?${params}`);
        if (res.data.success) {
          const orders = res.data.orders.map(o => {
            const safe = {};
            for (const k in o) safe[k] = typeof o[k] === 'object' && o[k] !== null ? JSON.stringify(o[k]) : o[k];
            return safe;
          });
          setOrderList(orders);
          setFilteredOrders(orders);
          if (id) {
            const match = orders.find(o => o.netsuite_id === id || o.netsuite_id === parseInt(id));
            if (match) { setSelectedOrder(match); fetchOrderDetails(id); }
          } else if (orders.length > 0) {
            setSelectedOrder(orders[0]);
            fetchOrderDetails(orders[0].netsuite_id);
          }
        }
      } catch { toast.error('Failed to load orders'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id, appliedFilters]);

  useEffect(() => {
    if (!searchText.trim()) { setFilteredOrders(orderList); return; }
    const q = searchText.toLowerCase();
    setFilteredOrders(orderList.filter(o =>
      [o.salesOrderNo, o.customerName, o.saleRepName, o.tranId]
        .some(v => String(v || '').toLowerCase().includes(q))
    ));
  }, [searchText, orderList]);

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoadingDetails(true);
      setOrderDetails(null);
      const res = await axios.get(`${API_URL}/order/${orderId}`);
      setOrderDetails(res.data);
      setOrderStatus(res.data.so?.orderStatus?.refName || 'Unknown');
      try {
        const fRes = await axios.get(`${API_URL}/order/${orderId}/files`);
        setAttachedFiles(fRes.data.success ? fRes.data.files : []);
      } catch { setAttachedFiles([]); }
    } catch { toast.error('Failed to load order details'); setOrderStatus('Unknown'); }
    finally { setLoadingDetails(false); }
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    fetchOrderDetails(order.netsuite_id);
    window.history.replaceState(null, '', `/response/${order.netsuite_id}${deptParam ? `?dept=${deptParam}` : ''}`);
  };

  const removeFromList = (netsuiteId) => {
    const next = filteredOrders.filter(o => o.netsuite_id !== netsuiteId);
    setOrderList(prev => prev.filter(o => o.netsuite_id !== netsuiteId));
    setFilteredOrders(next);
    if (next.length > 0) handleOrderSelect(next[0]);
    else navigate('/');
  };

  const handleApprove = async () => {
    if (!selectedOrder) return;
    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/order/${selectedOrder.netsuite_id}/respond`, {
        department, action: 'approve', remark: '', respondedBy: currentUser?.id || null
      });
      toast.success('อนุมัติคำสั่งซื้อเรียบร้อย');
      removeFromList(selectedOrder.netsuite_id);
    } catch { toast.error('เกิดข้อผิดพลาดในการอนุมัติ'); }
    finally { setSubmitting(false); }
  };

  const handleReviseSubmit = async () => {
    if (!selectedOrder) return;
    try {
      setSubmitting(true);
      await axios.post(`${API_URL}/order/${selectedOrder.netsuite_id}/respond`, {
        department, action: 'revise', remark, respondedBy: currentUser?.id || null
      });
      toast.success('ส่งคำสั่งซื้อไปแก้ไขเรียบร้อย');
      setShowRemarkModal(false);
      setRemark('');
      removeFromList(selectedOrder.netsuite_id);
    } catch { toast.error('เกิดข้อผิดพลาดในการส่งแก้ไข'); }
    finally { setSubmitting(false); }
  };

  useEffect(() => {
    if (actionParam && id && selectedOrder) {
      if (actionParam === 'approve') handleApprove();
      else if (actionParam === 'revise') setShowRemarkModal(true);
    }
  }, [actionParam, id, selectedOrder]);

  if (loading) return <PageLoader message="กำลังโหลดข้อมูล..." />;

  const { so, customerName, shippingAddresses, locations } = orderDetails || {};
  const hasFilters = Object.values(appliedFilters).some(v => v?.trim());
  const st = statusStyle(orderStatus);

  return (
    <div className="saleco-response-container">

      {/* ── Left panel: order list ── */}
      <div className="sc-list-panel">
        <div className="sc-list-panel__header">
          <h5><i className="bi bi-list-check me-2"></i>Sales Orders</h5>
          <div className="d-flex align-items-center gap-2">
            <span className="badge" style={{ background: '#EFF6FF', color: '#1D4ED8', fontWeight: 600 }}>
              {filteredOrders.length}
            </span>
            <button
              className={`btn btn-sm ${hasFilters ? 'btn-warning' : 'btn-outline-secondary'}`}
              onClick={() => setShowFilterModal(true)}
              title="ตัวกรอง"
            >
              <i className="bi bi-funnel"></i>
            </button>
          </div>
        </div>

        <div className="sc-list-panel__search">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="ค้นหา SO, ลูกค้า, Sales Rep..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </div>

        <div className="sc-list-panel__items">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: 32 }}></i>
              <div className="mt-2 small">ไม่พบรายการ</div>
            </div>
          ) : filteredOrders.map(order => (
            <button
              key={order.netsuite_id}
              className={`sc-order-item ${selectedOrder?.netsuite_id === order.netsuite_id ? 'active' : ''}`}
              onClick={() => handleOrderSelect(order)}
            >
              <div className="sc-oi-num">{safeRender(order.salesOrderNo)}</div>
              <div className="sc-oi-customer">{safeRender(order.customerName)}</div>
              <div className="sc-oi-foot">
                <span>{formatDate(order.salesOrderDate)}</span>
                <span className="sc-oi-amount">฿{formatCurrency(order.grandTotal)}</span>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                {safeRender(order.saleRepName)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Right panel: order detail ── */}
      <div className="sc-detail-panel">
        {!selectedOrder ? (
          <div className="d-flex align-items-center justify-content-center h-100 text-muted">
            <div className="text-center">
              <i className="bi bi-arrow-left-circle" style={{ fontSize: 40 }}></i>
              <div className="mt-3">เลือก Sales Order จากรายการด้านซ้าย</div>
            </div>
          </div>
        ) : loadingDetails ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="text-center text-muted">
              <div className="spinner-border text-primary mb-3" role="status"></div>
              <div>กำลังโหลด...</div>
            </div>
          </div>
        ) : (
          <div className="sc-detail-inner">

            {/* Header */}
            <div className="sc-detail-header">
              <div>
                <div className="sc-detail-header__title">
                  {safeRender(selectedOrder.tranId) !== 'N/A'
                    ? safeRender(selectedOrder.tranId)
                    : safeRender(selectedOrder.salesOrderNo)}
                </div>
                <div className="sc-detail-header__sub">
                  {safeRender(selectedOrder.tranId) !== 'N/A' && (
                    <span><i className="bi bi-file-text me-1"></i>{safeRender(selectedOrder.salesOrderNo)}</span>
                  )}
                  <span><i className="bi bi-calendar3 me-1"></i>{formatDate(selectedOrder.salesOrderDate)}</span>
                  <span><i className="bi bi-person me-1"></i>{safeRender(selectedOrder.saleRepName)}</span>
                </div>
              </div>
              <span className="sc-status" style={{ background: st.bg, color: st.color }}>
                {orderStatus}
              </span>
            </div>

            {/* Metric cards */}
            <div className="sc-metrics">
              <div className="sc-metric">
                <div className="sc-metric__label">ลูกค้า</div>
                <div className="sc-metric__value" title={customerName || safeRender(selectedOrder.customerName)}>
                  {customerName || safeRender(selectedOrder.customerName)}
                </div>
              </div>
              <div className="sc-metric">
                <div className="sc-metric__label">มูลค่ารวม</div>
                <div className="sc-metric__value" style={{ color: 'var(--color-success)' }}>
                  ฿{formatCurrency(selectedOrder.grandTotal)}
                </div>
              </div>
              <div className="sc-metric">
                <div className="sc-metric__label">วันที่</div>
                <div className="sc-metric__value">{formatDate(selectedOrder.salesOrderDate)}</div>
              </div>
              <div className="sc-metric">
                <div className="sc-metric__label">รายการสินค้า</div>
                <div className="sc-metric__value">{orderDetails?.items?.length || 0} รายการ</div>
              </div>
            </div>

            {/* Order info */}
            <div className="sc-card">
              <div className="sc-card__header">
                <i className="bi bi-info-circle-fill"></i>
                ข้อมูลคำสั่งซื้อ
              </div>
              <div className="sc-card__body">
                <div className="sc-info-grid">
                  <div className="sc-info-item">
                    <div className="sc-info-label">Sales Representative</div>
                    <div className="sc-info-value">{so?.salesRep?.refName || safeRender(selectedOrder.saleRepName)}</div>
                  </div>
                  <div className="sc-info-item">
                    <div className="sc-info-label">Department</div>
                    <div className="sc-info-value">{so?.department?.refName || '-'}</div>
                  </div>
                  <div className="sc-info-item">
                    <div className="sc-info-label">PO Number</div>
                    <div className="sc-info-value">{so?.otherRefNum || '-'}</div>
                  </div>
                  <div className="sc-info-item">
                    <div className="sc-info-label">Transaction Date</div>
                    <div className="sc-info-value">{so?.tranDate ? formatDate(so.tranDate) : '-'}</div>
                  </div>
                  {so?.custbody_ar_all_memo && (
                    <div className="sc-info-item" style={{ gridColumn: '1 / -1' }}>
                      <div className="sc-info-label">Memo</div>
                      <div className="sc-memo">{so.custbody_ar_all_memo}</div>
                    </div>
                  )}
                  {so?.custbodyar_so_memo2 && (
                    <div className="sc-info-item" style={{ gridColumn: '1 / -1' }}>
                      <div className="sc-info-label">Notes</div>
                      <div className="sc-memo">{so.custbodyar_so_memo2}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="sc-card">
              <div className="sc-card__header">
                <i className="bi bi-cart-fill"></i>
                รายการสินค้า
                <span className="sc-card__count">{orderDetails?.items?.length || 0}</span>
              </div>
              {!orderDetails?.items?.length ? (
                <div className="text-center py-4 text-muted small">ไม่มีรายการสินค้า</div>
              ) : (
                <>
                  <div className="sc-items-table">
                    {orderDetails.items.map((item, i) => (
                      <div key={i} className="sc-item-row">
                        <div className="sc-item-num">{i + 1}</div>
                        <div className="sc-item-info">
                          <div className="sc-item-name">
                            {orderDetails.itemMap?.[item.item?.id] || item.item?.id || `Item ${i + 1}`}
                          </div>
                          {item.description && <div className="sc-item-desc">{item.description}</div>}
                          <div className="sc-item-meta">
                            <span><i className="bi bi-box me-1"></i>จำนวน: {item.quantity}</span>
                            <span><i className="bi bi-tag me-1"></i>ราคา: ฿{formatCurrency(item.rate)}</span>
                            {locations?.[item.inventorylocation?.id] && (
                              <span><i className="bi bi-geo-alt me-1"></i>{locations[item.inventorylocation.id]}</span>
                            )}
                          </div>
                        </div>
                        <div className="sc-item-amount">
                          ฿{formatCurrency(parseFloat(item.quantity || 0) * parseFloat(item.rate || 0))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="sc-total-row">
                    <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>ยอดรวม</span>
                    <span className="amount">฿{formatCurrency(selectedOrder.grandTotal)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Shipping */}
            {shippingAddresses && Object.keys(shippingAddresses).length > 0 && (
              <div className="sc-card">
                <div className="sc-card__header">
                  <i className="bi bi-truck"></i>
                  ที่อยู่จัดส่ง
                </div>
                <div className="sc-card__body">
                  <div className="sc-info-value">{Object.values(shippingAddresses).join(', ')}</div>
                </div>
              </div>
            )}

            {/* Attachments */}
            {attachedFiles.length > 0 && (
              <div className="sc-card">
                <div className="sc-card__header">
                  <i className="bi bi-paperclip"></i>
                  ไฟล์แนบ
                  <span className="sc-card__count">{attachedFiles.length}</span>
                </div>
                <div className="sc-card__body">
                  <div className="sc-files">
                    {attachedFiles.map((file, i) => (
                      <div
                        key={i}
                        className="sc-file-row"
                        onClick={() => window.open(`${API_URL}/order/${selectedOrder.netsuite_id}/file/${file.filename}`, '_blank')}
                      >
                        <i className={`bi ${file.type === 'pdf' ? 'bi-file-earmark-pdf text-danger' : 'bi-file-earmark-image text-success'}`}></i>
                        <span className="sc-file-name">{file.filename}</span>
                        <span className="sc-file-meta">{file.type?.toUpperCase()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="sc-actions">
              <button
                className="btn btn-danger"
                onClick={() => setShowRemarkModal(true)}
                disabled={submitting}
              >
                <i className="bi bi-arrow-counterclockwise me-2"></i>กลับไปแก้ไข
              </button>
              <button
                className="btn btn-success"
                onClick={handleApprove}
                disabled={submitting}
              >
                {submitting ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span>กำลังทำงาน...</>
                ) : (
                  <><i className="bi bi-check-circle me-2"></i>ผ่าน</>
                )}
              </button>
            </div>

          </div>
        )}
      </div>

      {/* Remark modal */}
      <Modal show={showRemarkModal} onHide={() => setShowRemarkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>หมายเหตุการแก้ไข</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>กรุณาระบุเหตุผลที่ต้องการให้แก้ไข</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={remark}
              onChange={e => setRemark(e.target.value)}
              placeholder="ระบุรายละเอียดที่ต้องการให้แก้ไข..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemarkModal(false)} disabled={submitting}>ยกเลิก</Button>
          <Button variant="primary" onClick={handleReviseSubmit} disabled={submitting || !remark.trim()}>
            {submitting ? <><span className="spinner-border spinner-border-sm me-2"></span>กำลังส่ง...</> : 'ยืนยันการส่งแก้ไข'}
          </Button>
        </Modal.Footer>
      </Modal>

      <OrderFilter
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onApplyFilters={f => setAppliedFilters(f)}
        currentFilters={appliedFilters}
      />
    </div>
  );
};

export default SaleCoResponse;
